import React from "react";
import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import moment from "moment";
import { CW, Panel, Pill } from "./cwUi";

/**
 * P3.6 — "Coach checklist" (design PDF page 2, full width under the 3 columns).
 * Ticks are saved on the submission (reviewWork.checklist). "Complete
 * review work" does NOT send feedback — the check-in stays in Today as
 * "Feedback to send" until feedback is sent or marked not needed.
 */

export const CHECKLIST = [
  { key: "checkInReviewed", label: "Check-in reviewed", hint: "Answers and progress read" },
  { key: "programmeConsidered", label: "Programme considered", hint: "This week's tasks and comments" },
  { key: "nextStepsNoted", label: "Next steps noted", hint: "In the notebook or feedback" },
];

export default function ReviewChecklist({ checklist = {}, onToggle, onSave, onComplete, completedAt, busy, feedbackDone, saveState }) {
  const allTicked = CHECKLIST.every((item) => checklist[item.key]);
  const status = [
    saveState === "saving" ? "Saving checklist…" : saveState === "error" ? "Checklist not saved" : "Checklist saved",
    completedAt ? `Review work complete ${moment(completedAt).format("D MMM, HH:mm")}` : null,
    feedbackDone ? "Feedback done" : "Feedback not sent",
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <Panel
      title="Coach checklist"
      info="review.checklist"
      action={<Typography sx={{ fontSize: 12.5, color: saveState === "error" ? CW.red : CW.muted }}>{status}</Typography>}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr)) auto" },
          gap: { xs: 0.5, md: 2 },
          alignItems: "center",
        }}
      >
        {CHECKLIST.map((item) => (
          <FormControlLabel
            key={item.key}
            sx={{ m: 0 }}
            title={item.hint}
            control={
              <Checkbox
                size="small"
                checked={Boolean(checklist[item.key])}
                disabled={busy}
                onChange={(e) => onToggle(item.key, e.target.checked)}
              />
            }
            label={<Typography sx={{ fontSize: 14.5, color: CW.ink }}>{item.label}</Typography>}
          />
        ))}
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: { xs: 1, md: 0 } }}>
          <button
            type="button"
            className="small-contained-outlined-button"
            style={{ padding: "10px 22px" }}
            disabled={busy}
            onClick={onSave}
          >
            Save checklist
          </button>
          {completedAt ? (
            <Pill tone="green" sx={{ px: 2, py: 1.1, fontSize: 13 }}>
              Review work complete
            </Pill>
          ) : (
            <button
              type="button"
              className="small-contained-outlined-button"
              style={{ padding: "10px 22px", minWidth: 240 }}
              disabled={busy}
              onClick={onComplete}
              title={allTicked ? "" : "You can complete with items unticked"}
            >
              Mark review work complete
            </button>
          )}
        </Box>
      </Box>
      <Typography sx={{ fontSize: 12.5, color: CW.muted, mt: 1.5 }}>
        Saving or completing this checklist does not send feedback.
      </Typography>
    </Panel>
  );
}
